/**
 * AbstractDesignModal — pick an on-brand abstract background for a card.
 *
 * Two sources, both constrained to the RESILIGENT blue palette:
 *  - Procedural (instant, free): POST /cards/{id}/abstract/options returns a
 *    grid of generated gradient/geometric/organic/wave/contour designs.
 *  - AI (Nano Banana, costs credits): POST /cards/{id}/abstract/ai.
 *
 * Picking one calls the parent's onPick(url) which sets it as the card hero via
 * select-candidate (the url is already a cards/... plate, so it composes fast).
 */
import React, { useEffect, useState, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Skeleton } from "./ui/skeleton";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Loader2, Shapes, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";

const _API_BASE =
  typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL ||
      (typeof window !== "undefined" ? window.location.origin : "");
const API = `${_API_BASE}/api`;

const abs = (url) => (url?.startsWith("http") ? url : `${_API_BASE}${url}`);

function OptionTile({ opt, busy, onPick }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => onPick(opt)}
      className="relative group rounded-lg border border-slate-200 overflow-hidden hover:border-sky-500 transition aspect-square bg-slate-100 disabled:cursor-wait"
      data-testid={`abstract-option-${opt.style}`}
      title={`Use this ${opt.label} design`}
    >
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
          <Loader2 className="h-5 w-5 animate-spin text-sky-400" />
        </div>
      )}
      <img
        src={abs(opt.url)}
        alt={opt.label}
        onLoad={() => setLoaded(true)}
        className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
        loading="lazy"
      />
      <div className={`absolute inset-0 flex items-center justify-center transition ${busy ? "bg-black/50 opacity-100" : "bg-black/40 opacity-0 group-hover:opacity-100"}`}>
        {busy ? (
          <span className="text-white text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Applying…</span>
        ) : (
          <span className="text-white text-sm font-medium">Use this design</span>
        )}
      </div>
      <span className="absolute top-2 left-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white/85 text-slate-700">
        {opt.label}
      </span>
    </button>
  );
}

export default function AbstractDesignModal({ workspaceId, cardId, onPick, onClose }) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [picking, setPicking] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");

  const loadProcedural = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/abstract/options`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ count: 6 }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setOptions(data.options || []);
    } catch (e) {
      toast.error(`Could not generate designs: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, cardId]);

  useEffect(() => { loadProcedural(); }, [loadProcedural]);

  const generateAi = async () => {
    setAiBusy(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/abstract/ai`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: aiPrompt.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      if (data.option) setOptions((prev) => [data.option, ...prev]);
      toast.success("AI abstract added");
    } catch (e) {
      toast.error(`AI generation failed: ${e.message}`);
    } finally {
      setAiBusy(false);
    }
  };

  const handlePick = async (opt) => {
    setPicking(opt.url);
    try {
      await onPick(opt.url);
      onClose();
    } catch (e) {
      toast.error(e?.message || "Could not apply design");
    } finally {
      setPicking(null);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl w-full p-0 gap-0 max-h-[88vh] overflow-hidden flex flex-col" data-testid="abstract-design-modal">
        <div className="p-4 border-b border-slate-200">
          <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Shapes className="h-4 w-4 text-sky-600" /> Abstract brand designs
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-0.5">
            Always-on-brand abstract backgrounds in your RESILIGENT blue. Click one to set it as the card's hero.
          </p>
        </div>

        {/* AI + regenerate controls */}
        <div className="p-3 border-b border-slate-200 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={loadProcedural} disabled={loading} data-testid="abstract-regenerate-btn">
            {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            New options
          </Button>
          <div className="flex-1 min-w-[180px]">
            <Input
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Optional: describe an AI abstract (e.g. flowing network lines)"
              className="h-8 text-xs"
              data-testid="abstract-ai-prompt"
            />
          </div>
          <Button size="sm" onClick={generateAi} disabled={aiBusy} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="abstract-generate-ai-btn">
            {aiBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
            Generate AI abstract
          </Button>
        </div>

        {/* Options grid */}
        <div className="p-4 overflow-auto" data-testid="abstract-options-grid">
          {loading && options.length === 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-lg" />)}
            </div>
          ) : options.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500 text-sm">
              No designs yet — click "New options".
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {options.map((o, i) => (
                <OptionTile key={`${o.style}-${i}-${o.url}`} opt={o} busy={picking === o.url} onPick={handlePick} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
